/**
 * Projects kept in the organisation's database, as opposed to the ones in this browser.
 * Row-level security decides what comes back, so these queries ask for everything and the database
 * returns only what the signed-in person may see.
 */
import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { ProjectDoc } from '../model/types';

export interface Organisation {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  trialEndsAt: string | null;
}

export interface CloudProject {
  id: string;
  orgId: string;
  name: string;
  client: string | null;
  site: string | null;
  updatedAt: string;
  sourceCount: number;
}

const client = () => {
  if (!supabase) throw new Error('This build has no Supabase project configured.');
  return supabase;
};

/** Turns a database error into something worth showing a person. */
export function explain(error: PostgrestError | null, fallback: string): string {
  if (!error) return fallback;
  if (error.code === '42501' || error.message.includes('row-level security')) {
    return 'Your account does not have permission to do that in this organisation.';
  }
  if (error.code === 'PGRST301' || error.message.includes('JWT')) return 'Your sign-in has expired. Sign in again.';
  return error.message;
}

export async function listOrganisations(): Promise<{ organisations: Organisation[] } | { error: string }> {
  const { data, error } = await client()
    .from('memberships')
    .select('role, organisations!inner(id, name, trial_ends_at)');
  if (error) return { error: explain(error, 'Could not load your organisations.') };
  type Row = { role: Organisation['role']; organisations: { id: string; name: string; trial_ends_at: string | null } };
  const organisations = (data as unknown as Row[]).map((r) => ({
    id: r.organisations.id,
    name: r.organisations.name,
    role: r.role,
    trialEndsAt: r.organisations.trial_ends_at,
  }));
  return { organisations };
}

export async function createOrganisation(name: string): Promise<{ id: string } | { error: string }> {
  const { data, error } = await client().rpc('create_organisation', { org_name: name });
  if (error) return { error: explain(error, 'Could not create the organisation.') };
  return { id: data as string };
}

export async function listProjects(orgId: string): Promise<{ projects: CloudProject[] } | { error: string }> {
  const { data, error } = await client()
    .from('projects')
    .select('id, org_id, name, client, site, updated_at, project_drafts(doc)')
    .eq('org_id', orgId)
    .is('archived_at', null)
    .order('updated_at', { ascending: false });
  if (error) return { error: explain(error, 'Could not load the projects.') };
  type Row = { id: string; org_id: string; name: string; client: string | null; site: string | null; updated_at: string; project_drafts: Array<{ doc: ProjectDoc }> | { doc: ProjectDoc } | null };
  const projects = (data as unknown as Row[]).map((r) => {
    const draft = Array.isArray(r.project_drafts) ? r.project_drafts[0] : r.project_drafts;
    return {
      id: r.id, orgId: r.org_id, name: r.name, client: r.client, site: r.site, updatedAt: r.updated_at,
      sourceCount: draft?.doc?.sources?.length ?? 0,
    };
  });
  return { projects };
}

export async function loadProject(id: string): Promise<{ doc: ProjectDoc; version: number } | { error: string }> {
  const { data, error } = await client().from('project_drafts').select('doc, version').eq('project_id', id).maybeSingle();
  if (error) return { error: explain(error, 'Could not open the project.') };
  if (!data) return { error: 'That project has no saved contents yet.' };
  return { doc: data.doc as ProjectDoc, version: data.version as number };
}

export async function createProject(orgId: string, doc: ProjectDoc): Promise<{ id: string } | { error: string }> {
  const sb = client();
  const { data: user } = await sb.auth.getUser();
  const { data, error } = await sb
    .from('projects')
    .insert({ org_id: orgId, name: doc.meta.name, client: doc.meta.client ?? null, site: doc.meta.site ?? null, reference: doc.meta.reference ?? null, created_by: user.user?.id ?? null })
    .select('id')
    .single();
  if (error) return { error: explain(error, 'Could not create the project.') };
  const id = (data as { id: string }).id;
  const { error: draftError } = await sb.from('project_drafts').insert({ project_id: id, doc, updated_by: user.user?.id ?? null });
  if (draftError) return { error: explain(draftError, 'The project was created but its contents could not be saved.') };
  return { id };
}

/**
 * Saves the working copy. `version` is the version this edit started from: if someone else has saved
 * since, no rows match and the caller is told rather than quietly overwriting their work.
 */
export async function saveProject(id: string, doc: ProjectDoc, version: number): Promise<{ version: number } | { error: string; conflict?: boolean }> {
  const sb = client();
  const { data: user } = await sb.auth.getUser();
  const { data, error } = await sb
    .from('project_drafts')
    .update({ doc, version: version + 1, updated_by: user.user?.id ?? null, updated_at: new Date().toISOString() })
    .eq('project_id', id)
    .eq('version', version)
    .select('version');
  if (error) return { error: explain(error, 'Could not save.') };
  if (!data || data.length === 0) {
    return { error: 'Someone else has saved this project since you opened it. Reload it to see their changes.', conflict: true };
  }
  await sb.from('projects').update({ name: doc.meta.name, client: doc.meta.client ?? null, site: doc.meta.site ?? null, reference: doc.meta.reference ?? null }).eq('id', id);
  return { version: version + 1 };
}
